import { useParams, Link } from "react-router-dom";
import { Box, Typography, Breadcrumbs, Link as MuiLink } from "@mui/material";
import { blogs } from "./blogsData";
import AdSense from "../../components/AdSense";
import SEO from "../../components/SEO/SEO";
import AppDownloadCTA, {
    PageShell,
} from "../../components/AppDownloadCTA/AppDownloadCTA";
import { PhoneMockup } from "../../components/ScreenshotGallery/ScreenshotGallery";
import { SITE_NAME, SITE_URL } from "../../seo/constants";
import { APP_SCREENSHOTS } from "../../seo/assets";

export default function BlogPage() {
    const { slug } = useParams();
    const blog = blogs[slug];

    if (!blog) {
        return (
            <PageShell>
                <SEO
                    title="Blog not found"
                    description="Blog post not found."
                    path={`/blogs/${slug || ""}`}
                    noindex
                />
                <Typography variant="h4" component="h1">
                    Blog not found
                </Typography>
                <MuiLink component={Link} to="/blogs" sx={{ mt: 2, display: "inline-block" }}>
                    Browse all Islamic blogs
                </MuiLink>
            </PageShell>
        );
    }

    return (
        <Box
            sx={{
                display: "flex",
                justifyContent: "center",
                gap: 3,
                maxWidth: 1260,
                mx: "auto",
                px: 2,
                py: { xs: 3, md: 5 },
                background:
                    "linear-gradient(180deg, rgba(240,248,240,0.45) 0%, #ffffff 30%)",
            }}
        >
            <SEO
                title={`${blog.title} | ${SITE_NAME}`}
                description={blog.description}
                path={`/blogs/${slug}`}
                type="article"
                image={`${SITE_URL}/images/screenshots/screen-1.webp`}
                jsonLd={{
                    "@context": "https://schema.org",
                    "@type": "Article",
                    headline: blog.title,
                    description: blog.description,
                    author: { "@type": "Organization", name: SITE_NAME },
                    publisher: {
                        "@type": "Organization",
                        name: SITE_NAME,
                        logo: {
                            "@type": "ImageObject",
                            url: `${SITE_URL}/images/app-icon.png`,
                        },
                    },
                    mainEntityOfPage: `${SITE_URL}/blogs/${slug}`,
                }}
            />

            <Box
                className="side-ad"
                sx={{
                    width: 160,
                    flexShrink: 0,
                    position: "sticky",
                    top: 100,
                    alignSelf: "flex-start",
                    display: { xs: "none", lg: "block" },
                }}
            >
                <AdSense slot="9786570260" />
            </Box>

            <Box component="main" sx={{ maxWidth: 800, width: "100%" }}>
                <Breadcrumbs sx={{ mb: 2 }}>
                    <MuiLink component={Link} to="/" underline="hover" color="inherit">
                        Home
                    </MuiLink>
                    <MuiLink
                        component={Link}
                        to="/blogs"
                        underline="hover"
                        color="inherit"
                    >
                        Islamic Blogs
                    </MuiLink>
                    <Typography color="text.primary">{blog.title.split("–")[0].trim()}</Typography>
                </Breadcrumbs>

                <Typography
                    variant="h1"
                    component="h1"
                    sx={{
                        fontSize: { xs: "1.85rem", md: "2.4rem" },
                        fontWeight: 700,
                        mb: 2,
                        lineHeight: 1.2,
                        color: "text.primary",
                    }}
                >
                    {blog.title}
                </Typography>

                <Typography
                    variant="body1"
                    sx={{
                        mb: 3,
                        fontSize: "1.1rem",
                        lineHeight: 1.75,
                        color: "text.secondary",
                    }}
                >
                    {blog.description}
                </Typography>

                <Box sx={{ my: 3 }}>
                    <AdSense slot="2566597353" />
                </Box>

                {blog.sections.map((section, index) => (
                    <Box component="section" key={index} sx={{ mb: 3.5 }}>
                        <Typography
                            variant="h4"
                            component="h2"
                            sx={{
                                fontSize: { xs: "1.25rem", md: "1.45rem" },
                                fontWeight: 600,
                                mb: 1.25,
                                color: "primary.dark",
                            }}
                        >
                            {section.heading}
                        </Typography>
                        <Typography
                            variant="body1"
                            sx={{ lineHeight: 1.85, color: "text.secondary" }}
                        >
                            {section.content}
                        </Typography>

                        {(index + 1) % 2 === 0 && (
                            <Box sx={{ my: 3 }}>
                                <AdSense slot="2566597353" />
                            </Box>
                        )}
                    </Box>
                ))}

                <Box
                    sx={{
                        display: "flex",
                        gap: 3,
                        alignItems: "center",
                        flexWrap: "wrap",
                        mt: 4,
                        p: 3,
                        borderRadius: 3,
                        border: "1px solid rgba(46,139,87,0.12)",
                        background: "rgba(240,248,240,0.5)",
                    }}
                >
                    <PhoneMockup
                        src={APP_SCREENSHOTS[0].src}
                        alt="Quran Chat Buddy app"
                        maxWidth={140}
                    />
                    <Box sx={{ flex: 1, minWidth: 240 }}>
                        <Typography variant="h5" sx={{ mb: 1, fontWeight: 700 }}>
                            Continue learning with Quran Chat Buddy
                        </Typography>
                        <Typography
                            variant="body2"
                            sx={{ color: "text.secondary", mb: 1, lineHeight: 1.7 }}
                        >
                            Ask Quran questions with AI, track Salah prayer times, read
                            daily ayat, and grow your deen on iPhone or Android.
                        </Typography>
                    </Box>
                </Box>

                <AppDownloadCTA showIcon={false} />
            </Box>

            <Box
                className="side-ad"
                sx={{
                    width: 160,
                    flexShrink: 0,
                    position: "sticky",
                    top: 100,
                    alignSelf: "flex-start",
                    display: { xs: "none", lg: "block" },
                }}
            >
                <AdSense slot="9786570260" />
            </Box>
        </Box>
    );
}
